// src/scenes/LobbyScene.js
// v14 - Fixed ReferenceError in handleResize, uses UIController popups.

import { listenToGameUpdates, startGame, leaveGame, createGameSession } from "../services/multiplayerService.js";
import MenuUIUtils from "../ui/MenuUIUtils.js";
import BaseMenuScene from "./BaseMenuScene.js";
import { auth, db, realtimeDB, firebase } from "../firebaseConfig.js";
import { pollGameUpdates, getDbUrl, joinGame as joinGameRest, checkGameExists as checkGameExistsRest, createGame } from '../firebaseRestApi.js';
import claudeAPIService from "../services/claudeAPIService.js";
import UIController from "../ui/uiController.js";
import Phaser from 'phaser';

// --- Helper Functions ---
function waitForAuth() {
  return new Promise((resolve, reject) => {
    if (auth.currentUser) return resolve(auth.currentUser);
    const timer = setTimeout(() => { unsubscribe(); reject(new Error("Auth timeout")); }, 5000);
    const unsubscribe = auth.onAuthStateChanged(user => { clearTimeout(timer); unsubscribe(); resolve(user); }, error => { clearTimeout(timer); unsubscribe(); reject(error); });
  });
}
async function createGameWithFallback(userId, displayName, gameMode, timeLimit) {
    try { return await createGameSession(userId, displayName, gameMode, timeLimit); } catch (error) { console.warn("Std create failed, REST fallback:", error); return createGame(userId, displayName, gameMode, timeLimit); }
}
// --- End Helper Functions ---


class LobbyScene extends BaseMenuScene {
  constructor() {
    super({ key: "LobbyScene" });
    // Scene Properties
    this.gameCode = "";
    this.isHost = false;
    this.gameData = null;
    this.unsubscribe = null;
    this.uiElements = {};
    this.playerUiMap = new Map();
    this.isTransitioning = false;
    this.aiNameGenerated = false;
    this.teamName = "Team_Lobby";
    this.playerDisplayName = "Player";
    this.claudeApi = new claudeAPIService();
    this.isPopupOpen = false; // Managed by UIController

    // Layout Constants
    this.padding = 20;
    this.vSpacing = 15;
    this.buttonHeight = 40;
    this.buttonWidth = 200;

    // Bound event listeners for HTML popups (essential for correct removal)
    // REMOVED BINDINGS FOR HANDLERS NOW IN UICONTROLLER
    this.boundHideAiPopup = () => UIController.hidePopup('ai-name-popup'); // Use UIController directly
    this.boundHideCustomPopup = () => UIController.hidePopup('custom-name-popup'); // Use UIController directly
  }

  // --- Scene Lifecycle Methods ---

  init(data) { /* ... keep as before ... */
    console.log(`LobbyScene init: GC=${data.gameCode}, Host=${data.isHost}, Restart=${data.fromRestart}`); super.init(data); this.gameCode=data.gameCode||""; this.isHost=data.isHost||false; this.fromRestart=data.fromRestart||false; this.gameData=null; this.playerUiMap.clear(); this.isTransitioning=false; this.aiNameGenerated=false; this.teamName=`Team_${this.gameCode?this.gameCode.substring(0,3):'XYZ'}`; this.needsRejoin=this.fromRestart&&auth?.currentUser; this.playerDisplayName=auth?.currentUser?.email||"Player"; if(auth?.currentUser){db.collection("userProfiles").doc(auth.currentUser.uid).get().then(doc=>{if(doc.exists){this.playerDisplayName=doc.data().displayName||auth.currentUser.email;}}).catch(e=>{});}
  }

  // src/scenes/LobbyScene.js

// Replace the entire create method with this:
// src/scenes/LobbyScene.js

// Replace the entire create method with this:
// src/scenes/LobbyScene.js

// Replace the entire create method with this:
async create() {
    this.setRibbonVisibility(false);
    console.log("LobbyScene create:", this.gameCode);
    if (!this.gameCode) {
        console.warn("LobbyScene: No game code provided, returning to MultiplayerMenuScene.");
        sceneManager.switchToScene("MultiplayerMenuScene");
        return;
    }
    super.create(); // Handles background, adds resize listener
    this.clearMenuItems(); // Clear any inherited items
    UIController.setActiveScene(this); // Set active scene for UIController

    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    const centerX = width / 2;
    this.cameras.main.setBackgroundColor("#000000");

    // Create Non-Interactive Background Elements
    this.createBackgroundGraphics(width, height); // Assuming this helper exists

    // Reset UI Elements Store for this instance
    this.uiElements = {};

    // Layout Calculations (remain the same)
    let currentY = this.padding + 30;
    const titleY = currentY; currentY += 40 + this.vSpacing * 2;
    const codeLabelY = currentY; currentY += 18 + 5;
    const codeTextY = currentY + 8; currentY += 32 + this.padding;
    const playersLabelY = currentY; currentY += 18 + 10;
    const playersContainerY = currentY + (height * 0.25) / 2;
    const playerListMaxHeight = height * 0.25;
    currentY = playersContainerY + playerListMaxHeight / 2 + this.vSpacing * 2;
    const teamNameLabelY = currentY; currentY += 18 + 5;
    const teamNameTextY = currentY + 8; currentY += 24 + this.vSpacing;
    const teamButtonY = currentY + this.buttonHeight / 2;
    const actionButtonContainerY = height - 80;
    const actionSpacing = 60;

    // Create Static UI Elements (These are generally safe to create immediately)
    this.uiElements.title = this.createThemedText(centerX, titleY, "GAME LOBBY", '40px', MenuUIUtils.COLORS.secondary);
    this.uiElements.codeLabel = this.createThemedText(centerX, codeLabelY, "GAME CODE:", '18px', '#CCCCCC');
    this.uiElements.codeText = this.createThemedText(centerX, codeTextY, this.gameCode, '32px', '#00FFFF', '#111122');
    this.uiElements.codeBorder = this.createBorder(this.uiElements.codeText, 0x00FFFF, 0.5);
    this.uiElements.playersLabel = this.createThemedText(centerX, playersLabelY, "PLAYERS:", '18px', '#CCCCCC');
    this.playersContainer = this.add.container(centerX, playersContainerY);
    this.uiElements.playersContainer = this.playersContainer;
    this.uiElements.teamNameLabel = this.createThemedText(centerX, teamNameLabelY, "TEAM NAME:", '18px', '#CCCCCC');
    this.uiElements.teamNameText = this.createThemedText(centerX, teamNameTextY, this.teamName, '24px', '#00FFFF', '#111122');
    this.uiElements.teamNameBorder = this.createBorder(this.uiElements.teamNameText, 0x00FFFF, 0.4);
    this.uiElements.actionButtonContainer = this.add.container(centerX, actionButtonContainerY);

    // --- Deferred Button Creation ---
    // Use time.delayedCall with 0ms delay to run this code *after* the current 'create' stack completes
    this.time.delayedCall(1, () => {
        // --- Guard: Check if scene is still active ---
        if (!this || !this.scene || !this.scene.isActive) {
            console.warn("LobbyScene: Scene became inactive before deferred button creation.");
            return;
        }
        // --- End Guard ---

        // Host-Specific Buttons
        if (this.isHost) {
            const teamButtonSpacing = 220;
            console.log("Host detected, creating host buttons (deferred)...");

            const aiBtn = this.createStyledButton(centerX - teamButtonSpacing / 2, teamButtonY, "AI Generate", () => {
                if (this.aiNameGenerated) { this.showMessage("AI name used."); return; }
                UIController.showPopup('ai-name-popup');
            }, MenuUIUtils.COLORS.secondary, 180, 40);
            if (aiBtn) this.uiElements.aiNameGenButton = aiBtn; else console.error("FAILED TO CREATE AI GENERATE BUTTON (deferred)!");

            const customBtn = this.createStyledButton(centerX + teamButtonSpacing / 2, teamButtonY, "Set Custom", () => {
                UIController.showPopup('custom-name-popup');
            }, MenuUIUtils.COLORS.bgLight, 180, 40);
            if (customBtn) this.uiElements.customNameButton = customBtn; else console.error("FAILED TO CREATE SET CUSTOM BUTTON (deferred)!");
        }

        // Action Buttons (Start and Leave)
        const startBtnY = -actionSpacing / 2;
        const leaveBtnY = actionSpacing / 2;

        console.log("Attempting to create Start Game button (deferred)...");
        const startBtn = this.createStyledButton(0, startBtnY, "START GAME", async () => {
             if (!this || !this.input || !this.input.enabled) { console.warn(`[${this.scene.key}] START GAME CLICKED, BUT INPUT DISABLED or scene invalid!`); return; }
             if (!this.isHost || this.isTransitioning) return;
             const user = auth.currentUser;
             if (!user) { this.showMessage("Please sign in."); return; }

             if (startBtn) this.setButtonLoading(startBtn, "STARTING...");

             try {
                 const playerCount = this.gameData ? Object.keys(this.gameData.players || {}).length : 0;
                 await startGame(this.gameCode, user.uid, playerCount < 2);
             } catch (error) {
                 console.error(`[${this.scene.key}] Game start error:`, error);
                 this.showMessage(`Error starting game: ${error.message}`);
                 if(startBtn) this.resetButtonState(startBtn, "START GAME", MenuUIUtils.COLORS.success);
             }
         }, MenuUIUtils.COLORS.success, 250, 50);

        if (startBtn) {
            console.log("Start Game button created successfully (deferred).");
            startBtn.setVisible(this.isHost);
            this.uiElements.actionButtonContainer.add(startBtn); // Add to container
            this.uiElements.startBtn = startBtn;
        } else {
            console.error("FAILED TO CREATE START GAME BUTTON (deferred)!");
        }

        console.log("Attempting to create Leave Game button (deferred)...");
        const leaveBtn = this.createStyledButton(0, leaveBtnY, "LEAVE GAME", () => {
            if (!this || !this.input ||!this.input.enabled) { console.warn(`[${this.scene.key}] LEAVE GAME CLICKED, BUT INPUT DISABLED or scene invalid!`); return; }
            if (this.isTransitioning) return;
            this.isTransitioning = true;

            if (this.unsubscribe) { try { this.unsubscribe(); this.unsubscribe = null; } catch (e) { console.error("Error unsubscribing:", e); } }

            console.log("Attempting direct transition to MultiplayerMenuScene...");
            sceneManager.switchToScene("MultiplayerMenuScene");

            const user = auth?.currentUser;
            if (user) {
                leaveGame(this.gameCode, user.uid)
                    .then(() => console.log("Left game in Firebase (async)."))
                    .catch(e => console.error("Error leaving game in Firebase (async):", e));
            }
        }, MenuUIUtils.COLORS.danger, 250, 50);

        if (leaveBtn) {
            console.log("Leave Game button created successfully (deferred).");
            this.uiElements.actionButtonContainer.add(leaveBtn); // Add to container
            this.uiElements.leaveBtn = leaveBtn;
        } else {
            console.error("FAILED TO CREATE LEAVE GAME BUTTON (deferred)!");
        }

        // Initial state updates might be needed here if they depend on buttons existing
        this.updatePlayersList(); // Re-run this to potentially update start button state

    }, this); // End of delayedCall

    // --- Final Setup Steps (Run immediately) ---
    if (this.needsRejoin) {
        this.attemptRejoin();
    }
    // Initial player list population (will run again in delayed call)
    this.updatePlayersList();
    this.initMultiplayerListener(); // Start listening for updates
    this.handleResize(); // Ensure initial positioning of static elements

    console.log(`[${this.scene.key}] create method finished (deferred actions pending).`);
}


  // --- Helper Methods (Copied from BaseMenuScene for local availability) ---
  createBackgroundGraphics(width, height) { /* ... keep implementation ... */ const g=this.add.graphics({lineStyle:{width:1,color:0x003300,alpha:0.2}}); for(let x=0;x<width;x+=50)g.strokeLineShape(new Phaser.Geom.Line(x,0,x,height)); for(let y=0;y<height;y+=50)g.strokeLineShape(new Phaser.Geom.Line(0,y,width,y)); g.setDepth(-1); this.createBackgroundParticles(15,0x00FFFF); }
  createBackgroundParticles(count = 20, color = 0x00FF00){ /* ... keep implementation ... */ try { const w = this.cameras?.main?.width || 800; const h = this.cameras?.main?.height || 600; if (!this.tweens || !this.add?.circle) return; for (let i = 0; i < count; i++) { if (!this.scene?.systems?.scenePlugin?.isActive(this.scene.key)) return; const p=this.add.circle(Phaser.Math.Between(0,w),Phaser.Math.Between(0,h),Phaser.Math.Between(1,3),color,Phaser.Math.FloatBetween(0.05,0.2)).setDepth(-1); const t=this.tweens.add({targets:p,y:p.y-Phaser.Math.Between(100,300),alpha:0,duration:Phaser.Math.Between(10000,20000),repeat:-1,repeatDelay:Phaser.Math.Between(0,5000),onRepeat:()=>{if(!p?.scene||!p.active){t?.remove();return;}const cH=this.cameras?.main?.height||600; const cW=this.cameras?.main?.width||800; p.y=cH+10;p.x=Phaser.Math.Between(0,cW);p.alpha=Phaser.Math.FloatBetween(0.05,0.2);}});}} catch (e) {} }
  createThemedText(x, y, text, fontSize, color, bgColor = null) { /* ... keep implementation ... */ const style={fontFamily:"monospace",fontSize:fontSize,color:color,align:"center"}; if(bgColor){style.backgroundColor=bgColor;style.padding={x:15,y:8};} return this.add.text(x,y,text,style).setOrigin(0.5); }
  createBorder(textObject, color, alpha) { /* ... keep implementation ... */ if(!textObject?.active)return this.add.graphics(); const b=textObject.getBounds(); const border=this.add.graphics().lineStyle(1,color,alpha).strokeRect(0,0,b.width+10,b.height+10); border.setPosition(textObject.x-b.width/2-5,textObject.y-b.height/2-5); border.setDepth(textObject.depth-1); return border; }
  createStyledButton(x, y, text, callback, color, width = 180, height = 40) {
    // Use centralized implementation from MenuUIUtils
    return MenuUIUtils.createStyledButton(this, x, y, text, callback, {
        bgColor: color,
        width: width,
        height: height,
        fontSize: '16px' // Consistent font size
    });
}
  
// Replace them with the updated implementations
setButtonLoading(buttonContainer, loadingText, disable = true) {
    if (!buttonContainer || !buttonContainer.active) return;
    
    try {
        // Get button parts - support both MenuUIUtils and custom buttons
        const bg = buttonContainer.getData?.('bg') || buttonContainer.getData?.('rectGraphic');
        const text = buttonContainer.getData?.('label') || buttonContainer.getData?.('textObject');
        const hitZone = buttonContainer.getData?.('hitZone');
        
        // Apply loading state using MenuUIUtils colors
        if (bg && typeof bg.fillStyle === 'function') {
            bg.clear().fillStyle(MenuUIUtils.COLORS.bgLight, 0.7);
            if (bg.fillRoundedRect) {
                bg.fillRoundedRect(
                    -buttonContainer.width / 2, -buttonContainer.height / 2,
                    buttonContainer.width, buttonContainer.height,
                    MenuUIUtils.SPACING.xs
                );
            }
        }
        
        // Update text if available
        if (text && typeof text.setText === 'function') {
            text.setText(loadingText);
            if (typeof text.setColor === 'function') {
                text.setColor(Phaser.Display.Color.ValueToColor(MenuUIUtils.COLORS.textDark).rgba);
            }
        }
        
        // Handle interactivity
        if (disable) {
            if (hitZone && typeof hitZone.disableInteractive === 'function') {
                hitZone.disableInteractive();
            } else if (buttonContainer.input) {
                buttonContainer.disableInteractive();
            }
        } else {
            if (hitZone && typeof hitZone.setInteractive === 'function') {
                hitZone.setInteractive({ useHandCursor: true });
            } else if (buttonContainer.input) {
                buttonContainer.setInteractive({ useHandCursor: true });
            }
        }
    } catch (e) {
        console.error("Error in setButtonLoading:", e);
    }
}

resetButtonState(buttonContainer, defaultText, defaultColor = null) {
    if (!buttonContainer || !buttonContainer.active) return;
    
    try {
        // Get button parts - support both MenuUIUtils and custom buttons
        const bg = buttonContainer.getData?.('bg') || buttonContainer.getData?.('rectGraphic');
        const text = buttonContainer.getData?.('label') || buttonContainer.getData?.('textObject');
        const hitZone = buttonContainer.getData?.('hitZone');
        const originalColor = buttonContainer.getData?.('originalColor');
        
        // Determine final color to use
        const finalColor = defaultColor || originalColor || MenuUIUtils.COLORS.primary;
        
        // Apply normal state
        if (bg && typeof bg.fillStyle === 'function') {
            bg.clear().fillStyle(finalColor, 1);
            if (bg.fillRoundedRect) {
                bg.fillRoundedRect(
                    -buttonContainer.width / 2, -buttonContainer.height / 2,
                    buttonContainer.width, buttonContainer.height,
                    MenuUIUtils.SPACING.xs
                );
            }
        }
        
        // Update text
        if (text && typeof text.setText === 'function') {
            text.setText(defaultText);
            if (typeof text.setColor === 'function') {
                const textColor = buttonContainer.getData?.('textColor') || MenuUIUtils.COLORS.textLight;
                text.setColor(Phaser.Display.Color.ValueToColor(textColor).rgba);
            }
        }
        
        // Re-enable interactivity
        if (hitZone && typeof hitZone.setInteractive === 'function') {
            hitZone.setInteractive({ useHandCursor: true });
        } else if (buttonContainer.input) {
            buttonContainer.setInteractive({ useHandCursor: true });
        }
    } catch (e) {
        console.error("Error in resetButtonState:", e);
    }
}
  
  showMessage(message, duration = 2000){ /* ... keep implementation ... */ if(this.scene?.isActive?.(this.scene.key)){const msg=this.add.text(this.cameras.main.width/2,50,message,{fontSize:'18px',color:'#FF5555',backgroundColor:'#000000CC',padding:{x:15,y:8},align:'center',fontFamily:'monospace'}).setOrigin(0.5).setDepth(10000); this.time.delayedCall(duration,()=>{if(msg?.active)msg.destroy();});}else{console.warn("Msg on inactive scene:",message);} }


  // --- Popup Management (Delegates to UIController) ---
  showPopup(popupId) { UIController.showPopup(popupId); }
  hidePopup(popupId) { UIController.hidePopup(popupId); }
  showAiNamePopup() { if (!this.isHost) return; if (this.aiNameGenerated) { this.showMessage("AI name used."); return; } UIController.showPopup('ai-name-popup'); }
  showCustomNamePopup() { if (!this.isHost) return; UIController.showPopup('custom-name-popup'); const input = document.getElementById('custom-team-name'); if(input) input.value = this.teamName; }

  // --- Core Logic Methods ---
// --- Core Logic Methods ---
async attemptRejoin() { /* ... keep as before ... */
  console.log("Attempting rejoin..."); try { await joinGameRest(this.gameCode, auth.currentUser.uid, this.playerDisplayName); console.log("Rejoined via REST"); } catch (error) { this.showMessage("Error rejoining."); }
}

initMultiplayerListener() { /* ... keep as before using pollGameUpdates ... */
  console.log(`Setting up poll for: ${this.gameCode}`); try { if (this.unsubscribe) { this.unsubscribe(); this.unsubscribe = null; } this.unsubscribe = pollGameUpdates(this.gameCode, (gameData) => { if (!this.scene.isActive("LobbyScene")) { if (this.unsubscribe) { this.unsubscribe(); this.unsubscribe = null; } return; } if (!gameData) { this.handleNullGameData(); return; } if (gameData.error) { console.error("Error in poll data:", gameData); return; } this.gameData = gameData; this.updatePlayersList(); if (gameData.status === "playing") { this.transitionToGame(); } }, 1500); } catch (error) { console.error("Error setting up poll listener:", error); this.showMessage("Error connecting."); }
}

async handleNullGameData() { /* ... keep as before ... */
  console.warn("Null data from poll."); const exists = await this.checkGameExistsWithREST(); if (!exists && this.scene.isActive("LobbyScene")) { this.showMessage("Game not found."); this.time.delayedCall(2000, () => { if (this.scene.isActive("LobbyScene")) { if (this.unsubscribe) this.unsubscribe(); sceneManager.switchToScene("MultiplayerMenuScene"); } }); }
}

async checkGameExistsWithREST() { /* ... keep as before ... */
  if (!this.gameCode) return false; try { return await checkGameExistsRest(this.gameCode); } catch (error) { return false; }
}

// This ensures the team name is correctly passed to the game scene
transitionToGame() { 
    if (this.isTransitioning) return; 
    this.isTransitioning = true; 
    console.log("Transitioning to MP Game..."); 
  
    // --- FIX: Unsubscribe BEFORE starting next scene ---
    if (this.unsubscribe) {
      try { this.unsubscribe(); } catch(e){}
      this.unsubscribe = null;
    }
    // --- END FIX ---
    
    this.time.delayedCall(100, () => { 
      if (!this.scene.isActive("LobbyScene")) { 
        this.isTransitioning = false; 
        return; 
      } 
      
      const sceneKey = "MultiplayerGameScene"; 
      if (this.scene.manager.keys[sceneKey]) { 
        // IMPORTANT: Pass the team name data to the game scene
        this.startScene(sceneKey, { 
          gameCode: this.gameCode, 
          isHost: this.isHost, 
          gameMode: this.gameData?.gameMode, 
          timeLimit: this.gameData?.timeLimit, 
          teamName: this.teamName // Ensure team name is passed
        }); 
      } else { 
        console.error(`Scene ${sceneKey} not found!`); 
        this.showMessage("Error loading game."); 
        this.isTransitioning = false; 
        this.startScene("MultiplayerMenuScene"); 
      }
    });
  }
setButtonLoading(buttonContainer, loadingText, disable = true) {
    if (!buttonContainer) return;
    
    try {
        // Get button parts
        const rect = buttonContainer.getData?.('rect');
        const text = buttonContainer.getData?.('text');
        
        // Apply loading state using MenuUIUtils colors
        if (rect) {
            rect.setFillStyle(MenuUIUtils.COLORS.bgLight);
            if (disable) rect.disableInteractive();
            else rect.setInteractive();
        }
        
        // Update text
        if (text) text.setText(loadingText);
    } catch (e) {
        console.error("Error in setButtonLoading:", e);
    }
}

/**
 * Reset button state with centralized method
 */
resetButtonState(buttonContainer, defaultText, defaultColor = null) {
    if (!buttonContainer) return;
    
    try {
        // Get button parts
        const rect = buttonContainer.getData?.('rect');
        const text = buttonContainer.getData?.('text');
        const originalColor = buttonContainer.getData?.('originalColor');
        
        // Determine final color to use
        const finalColor = defaultColor || originalColor || MenuUIUtils.COLORS.primary;
        
        // Apply normal state
        if (rect) {
            rect.setFillStyle(finalColor);
            rect.setInteractive();
        }
        
        // Update text
        if (text) text.setText(defaultText);
    } catch (e) {
        console.error("Error in resetButtonState:", e);
    }
}

  // Called BY UIController
// Replace this in the generateAiTeamName method in LobbyScene.js
// In LobbyScene.js

async generateAiTeamName(keywords = []) {
  console.log("LobbyScene: generateAiTeamName CB with keywords:", keywords);

  // --- Input Validation ---
  if (!Array.isArray(keywords) || keywords.length !== 3) {
      console.error("Invalid keywords received:", keywords);
      this.showMessage("Invalid keywords provided.");
      return;
  }
  if (keywords.some(k => !k || k.trim() === '')) {
      console.error("Empty keyword detected:", keywords);
      this.showMessage("Keywords cannot be empty.");
      return;
  }
  if (!this.isHost) {
      console.log("Skipping AI name generation (not host).");
      return;
  }
  if (this.aiNameGenerated) {
      console.log("Skipping AI name generation (already generated).");
      this.showMessage("AI name already used for this lobby.");
      return;
  }
  // --- End Validation ---

  const aiButton = this.uiElements.aiNameGenButton;
  if (!aiButton) {
      console.error("AI Generate button not found in uiElements.");
      this.showMessage("UI Error: Cannot find button.");
      return;
  }
  this.setButtonLoading(aiButton, "Generating..."); // Use inherited helper

  try {
      // Get current player names for context
      const playerNames = (this.gameData?.players && Object.keys(this.gameData.players).length > 0)
          ? Object.values(this.gameData.players).map(p => p.name || "Player").join(', ')
          : (this.playerDisplayName || "Player");

      // --- Creative Prompt ---
      const prompt = `
You are a witty and creative naming bot for a team in a fast-paced consulting space arcade game.
The game involves deflecting asteroids, navigating hyperspace, and using keyboard shortcuts.

Team Context:
- Players currently in lobby: ${playerNames}
- The team provided these 3 keywords for inspiration: ${keywords.join(', ')}

Your Task:
1.  Analyze the *vibe* and *implied personality* of the 3 keywords (${keywords.join(', ')}). Are they suggesting something aggressive, mysterious, speedy, technical, funny, chaotic, precise, etc.?
2.  Combine this inferred vibe with the US office, consulting, MBB, Big 4, strategy theme.
3.  Generate ONE medium, punchy, and highly creative team name (ideally 3-4 words, max 5). Make it sound cool and memorable! Avoid generic names.

IMPORTANT: Respond ONLY with the generated team name in ALL CAPS. Do not include explanations, quotes, any keywords, or any other text in the generated team name.
      `;
      // --- End Creative Prompt ---

      console.log("Sending prompt to Claude API via proxy...");

      // Create a fresh API service instance each time for safety
      this.claudeApi = new claudeAPIService();

      // Call the API service
      const response = await this.claudeApi.callClaudeApiWithRetry(prompt, 40); // Increased maxTokens slightly

      let generatedName = "";
      if (response) {
          generatedName = this.claudeApi.extractResponse(response).trim();
          console.log("Raw response from Claude via proxy:", generatedName); // Log raw response
      } else {
           console.warn("Received null or undefined response from API service.");
           throw new Error("No response from API service"); // Trigger fallback
      }

      // Validate returned name (ALL CAPS, reasonable length, no weird chars)
      if (generatedName && /^[A-Z0-9\s]+$/.test(generatedName) && generatedName.length > 1 && generatedName.length <= 30) {
          console.log("Valid AI name received:", generatedName);
          this.updateTeamName(generatedName); // Update UI and DB
          this.aiNameGenerated = true;
          // Update button state permanently
          this.setButtonLoading(aiButton, "AI Name Used", true); // Disable button
          const rect = aiButton?.getData?.('rect');
          if (rect) rect.setFillStyle(MenuUIUtils.COLORS.bgMedium); // Grey out used button

      } else {
          console.warn("Received invalid or empty name from AI:", `"${generatedName}"`);
          throw new Error("Invalid name format from AI"); // Trigger fallback
      }

  } catch (apiError) {
      console.error("Error generating AI team name (API call, extraction, or validation failed):", apiError);
      // Use an improved fallback name generator if API fails
      const playerNamesString = (this.gameData?.players && Object.keys(this.gameData.players).length > 0)
          ? Object.values(this.gameData.players).map(p => p.name || "Player").join(', ')
          : (this.playerDisplayName || "Player");
      const fallbackName = this.generateFallbackTeamName(keywords, playerNamesString);
      this.updateTeamName(fallbackName);
      this.showMessage(`AI unavailable. Using: ${fallbackName}`);
      this.resetButtonState(aiButton, "AI Generate", MenuUIUtils.COLORS.secondary); // Let them try again later maybe
      // DO NOT set aiNameGenerated = true here, allow retry
  }
}

// --- END of generateAiTeamName method ---
// In LobbyScene.js, add this method (likely after generateAiTeamName)

// Helper function for better fallback names
generateFallbackTeamName(keywords, playerNames) {
  const templates = [
      `The ${keywords[0]} ${keywords[1]}s`,
      `Galactic ${keywords[2]}`,
      `${keywords[0]} Raiders`,
      `Team ${keywords[1]}`,
      `Star ${keywords[2]} Crew`,
      `The ${keywords[0]} Spectres`,
      `Cosmic ${keywords[1]}`,
       // Add more creative templates
       `${keywords[0]} Velocity`,
       `Guardians of ${keywords[1]}`,
       `The ${keywords[2]} Paradox`
  ];
  // Use player names and keywords hash for a pseudo-random but consistent choice
  const combinedString = keywords.join('') + playerNames;
  let hash = 0;
  for (let i = 0; i < combinedString.length; i++) {
      const char = combinedString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash |= 0; // Convert to 32bit integer
  }
  const index = Math.abs(hash) % templates.length;
  let name = templates[index];
  // Basic cleanup - capitalize words and ensure ALL CAPS
  name = name.split(' ').map(word => word.toUpperCase()).join(' ');
  // Ensure max length and valid format
  name = name.replace(/[^A-Z0-9\s]/gi, '').trim(); // Remove invalid chars
  return name.substring(0, 25); // Ensure max length
}
  // Called BY UIController
  updateTeamName(newName) { /* ... keep implementation ... */
      console.log(`LobbyScene: updateTeamName CB with '${newName}'`); if (!newName) return; this.teamName = newName; if (this.uiElements.teamNameText?.active) { this.uiElements.teamNameText.setText(this.teamName); if (this.uiElements.teamNameBorder?.active) { const bounds = this.uiElements.teamNameText.getBounds(); this.uiElements.teamNameBorder.clear().lineStyle(1, 0x00FFFF, 0.4).strokeRect(0, 0, bounds.width + 10, bounds.height + 10); this.uiElements.teamNameBorder.setPosition(this.uiElements.teamNameText.x - bounds.width / 2 - 5, this.uiElements.teamNameText.y - bounds.height / 2 - 5); } } console.log("Updated team name UI to:", this.teamName); if (this.isHost && this.gameCode) { realtimeDB.ref(`games/${this.gameCode}`).update({ teamName: this.teamName }).catch(error => console.error("Error saving team name:", error)); }
  }

 // Replace the updatePlayersList method in LobbyScene.js with this improved version:

// START OF REPLACEMENT CODE for updatePlayersList
updatePlayersList() {
    // Guard clause: Ensure container exists and scene is active
    if (!this.playersContainer || !this.playersContainer.scene || !this.scene?.systems?.scenePlugin?.isActive(this.scene.key)) {
        // console.warn("updatePlayersList: Container/Scene not ready or inactive."); // Optional log
        return;
    }

    const newPlayersData = this.gameData?.players || {};
    const currentPlayerIds = Object.keys(newPlayersData);
    const existingPlayerIds = Array.from(this.playerUiMap.keys());
    const currentUserId = auth?.currentUser?.uid || '';

    // --- Team Name Update ---
    if (this.gameData?.teamName) {
        if (this.uiElements.teamNameText && this.gameData.teamName !== this.uiElements.teamNameText.text) {
            this.teamName = this.gameData.teamName;
            this.uiElements.teamNameText.setText(this.teamName);
            if (this.uiElements.teamNameBorder?.active) {
                try {
                    const bounds = this.uiElements.teamNameText.getBounds();
                    this.uiElements.teamNameBorder.clear().lineStyle(1, 0x00FFFF, 0.4).strokeRect(0, 0, bounds.width + 10, bounds.height + 10);
                    this.uiElements.teamNameBorder.setPosition(this.uiElements.teamNameText.x - bounds.width / 2 - 5, this.uiElements.teamNameText.y - bounds.height / 2 - 5);
                } catch(e) { console.warn("Error updating team name border:", e); }
            }
        }
    }
    // --- End Team Name Update ---

    // --- Player List Diffing ---
    const itemSpacing = 45;
    const itemHeight = 40;
    const itemWidth = 350;

    // Remove players who left
    existingPlayerIds.forEach(playerId => {
        if (!newPlayersData[playerId]) {
            const ui = this.playerUiMap.get(playerId);
            if (ui) { try { ui.bg?.destroy(); ui.nameText?.destroy(); ui.statusText?.destroy(); } catch (e) {} }
            this.playerUiMap.delete(playerId);
        }
    });

    // Update or add current players
    currentPlayerIds.forEach((playerId, index) => {
        const playerData = newPlayersData[playerId];
        const y = index * itemSpacing;
        const isHostPlayer = playerData.isHost ? "👑 " : "";
        const isCurrentUser = playerId === currentUserId;
        const statusTextValue = playerData.status || "waiting";
        const statusColor = statusTextValue === 'waiting' ? '#FFFF00' : '#00FF00';

        let ui = this.playerUiMap.get(playerId);

        if (ui) { // Update existing
             if (ui.bg?.active && ui.nameText?.active && ui.statusText?.active) {
                 if (this.tweens && Math.abs(ui.bg.y - y) > 1) {
                      this.tweens.add({ targets: [ui.bg, ui.nameText, ui.statusText], y: y, duration: 200, ease: 'Sine.easeInOut' });
                 } else {
                      ui.bg.y = y; ui.nameText.y = y; ui.statusText.y = y;
                 }
                 ui.nameText.setText(`${isHostPlayer}${playerData.name || 'Player'}`);
                 ui.nameText.setColor(isCurrentUser ? "#00FF00" : "#FFFFFF");
                 ui.statusText.setText(statusTextValue);
                 ui.statusText.setColor(statusColor);
                 ui.bg.setFillStyle(isCurrentUser ? 0x204020 : (index % 2 === 0 ? 0x282828 : 0x303030), 0.8);
                 ui.bg.setStrokeStyle(isCurrentUser ? 2 : 0, 0x00FF00);
            } else {
                console.warn(`UI elements for player ${playerId} inactive/missing, will recreate.`);
                if(ui) { try { ui.bg?.destroy(); ui.nameText?.destroy(); ui.statusText?.destroy(); } catch(e){} }
                this.playerUiMap.delete(playerId);
                ui = null; // Force recreation below
            }
        }

        if (!ui) { // Create new
            const itemBg = this.add.rectangle(0, y, itemWidth, itemHeight, isCurrentUser ? 0x204020 : (index % 2 === 0 ? 0x282828 : 0x303030), 0.8).setOrigin(0.5);
            if (isCurrentUser) itemBg.setStrokeStyle(2, 0x00FF00);
            const nameText = this.add.text(-itemWidth / 2 + 15, y, `${isHostPlayer}${playerData.name || 'Player'}`, { fontFamily: "monospace", fontSize: "18px", color: isCurrentUser ? "#00FF00" : "#FFFFFF", align: "left" }).setOrigin(0, 0.5);
            const statusText = this.add.text(itemWidth / 2 - 15, y, statusTextValue, { fontFamily: "monospace", fontSize: "16px", color: statusColor, align: "right" }).setOrigin(1, 0.5);

            itemBg.setData('isPlayerBg', true);
            itemBg.setData('nameText', nameText);
            itemBg.setData('statusText', statusText);

            this.playersContainer.add([itemBg, nameText, statusText]);
            this.playerUiMap.set(playerId, { bg: itemBg, nameText, statusText });
        }
    });
    // --- End Player List Diffing ---


    // --- FIX: Added Safety Checks for Start Button Logic ---
    if (this.isHost) {
        // Ensure scene is still active before accessing anything
        if (!this.scene?.systems?.scenePlugin?.isActive(this.scene.key)) {
            // console.log("updatePlayersList: Scene inactive, skipping start button update.");
            return; // Stop processing if scene is shutting down
        }

        const startBtnContainer = this.uiElements?.startBtn; // Use optional chaining

        // Check if the button container exists AND is still part of the active scene AND is active itself
        if (startBtnContainer && startBtnContainer.scene === this && startBtnContainer.active) {
            // Get the internal graphic and text using the keys from MenuUIUtils
            const rect = startBtnContainer.getData?.('rectGraphic'); // Correct key
            const text = startBtnContainer.getData?.('textObject'); // Correct key

            if (rect && text) { // Check internal elements exist
                const canStart = currentPlayerIds.length >= 2;

                if (canStart) {
                    // Enable button - Use MenuUIUtils helpers for consistency
                    MenuUIUtils.resetButtonState(startBtnContainer, "START GAME", MenuUIUtils.COLORS.success);
                    console.debug("Lobby Start button updated: START GAME (Enabled)");
                } else {
                    // Disable button - Use MenuUIUtils helpers for consistency
                    MenuUIUtils.setButtonLoading(startBtnContainer, "WAITING (2+)...", true); // 'true' disables interaction
                    console.debug("Lobby Start button updated: WAITING (Disabled)");
                }
            } else {
                // This might happen briefly during creation/destruction, usually okay unless persistent
                console.warn("Start button UI internal elements (rect/text) missing in updatePlayersList.");
            }
        } else {
             // Log if the button container itself is missing or belongs to another scene (common during transitions)
             // console.warn("Start button container is missing, inactive, or belongs to wrong scene in updatePlayersList."); // Reduce noise, uncomment if needed
        }
    }
    // --- END FIX ---
}
// END OF REPLACEMENT CODE for updatePlayersList




// In LobbyScene.js

// In LobbyScene.js

// In LobbyScene.js
handleResize(gameSize) {
    // --- Call the IMMEDIATE parent (BaseMenuScene) handleResize FIRST ---
    // BaseMenuScene handles repositioning title and base menu items if they exist
    super.handleResize(gameSize);
    // --- END PARENT CALL ---
  
    // Guard clause for this scene's elements
    if (!this.cameras?.main || !this.scene?.systems?.scenePlugin?.isActive(this.scene.key)) {
        return;
    }
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;
    const centerX = width / 2;
  
    // --- LobbyScene specific elements (use uiElements references) ---
    // Recalculate Y positions (local to this function for clarity)
    let currentY = this.padding + 30;
    const titleY = currentY; currentY += 40 + this.vSpacing * 2;
    const codeLabelY = currentY; currentY += 18 + 5;
    const codeTextY = currentY + 8; currentY += 32 + this.padding;
    const playersLabelY = currentY; currentY += 18 + 10;
    const playersContainerY = currentY + (height * 0.25) / 2;
    const playerListMaxHeight = height * 0.25; currentY = playersContainerY + playerListMaxHeight / 2 + this.vSpacing * 2;
    const teamNameLabelY = currentY; currentY += 18 + 5;
    const teamNameTextY = currentY + 8; currentY += 24 + this.vSpacing;
    const teamButtonY = currentY + this.buttonHeight / 2;
    const actionButtonContainerY = height - 80;
  
    // Reposition Lobby specific UI elements using calculated Ys
    // NOTE: BaseMenuScene.handleResize might have already positioned the title if
    // this.titleText was assigned in BaseMenuScene setup. Check if redundant.
    if(this.uiElements.title && this.uiElements.title.active) { this.uiElements.title.setPosition(centerX, titleY); }
  
    if(this.uiElements.codeLabel){ this.uiElements.codeLabel.setPosition(centerX, codeLabelY); }
    if(this.uiElements.codeText){
        this.uiElements.codeText.setPosition(centerX, codeTextY);
        if(this.uiElements.codeBorder?.active){ /* ... border logic ... */
            const b = this.uiElements.codeText.getBounds();
            this.uiElements.codeBorder.setPosition(centerX - b.width/2 - 5, codeTextY - b.height/2 - 5)
                .clear().lineStyle(1, 0x00FFFF, 0.5).strokeRect(0, 0, b.width + 10, b.height + 10);
        }
    }
    if(this.uiElements.playersLabel){ this.uiElements.playersLabel.setPosition(centerX, playersLabelY); }
    if(this.uiElements.playersContainer){ this.uiElements.playersContainer.setPosition(centerX, playersContainerY); } // Container position
    if(this.uiElements.teamNameLabel){ this.uiElements.teamNameLabel.setPosition(centerX, teamNameLabelY); }
    if(this.uiElements.teamNameText){
        this.uiElements.teamNameText.setPosition(centerX, teamNameTextY);
        if(this.uiElements.teamNameBorder?.active){ /* ... border logic ... */
            const b = this.uiElements.teamNameText.getBounds();
            this.uiElements.teamNameBorder.setPosition(centerX - b.width/2 - 5, teamNameTextY - b.height/2 - 5)
                .clear().lineStyle(1, 0x00FFFF, 0.4).strokeRect(0, 0, b.width + 10, b.height + 10);
        }
    }
    if(this.isHost){ // Reposition host-only buttons
        const spacing = 220;
        if(this.uiElements.aiNameGenButton){ this.uiElements.aiNameGenButton.setPosition(centerX - spacing/2, teamButtonY); }
        if(this.uiElements.customNameButton){ this.uiElements.customNameButton.setPosition(centerX + spacing/2, teamButtonY); }
    }
    if(this.uiElements.actionButtonContainer){ this.uiElements.actionButtonContainer.setPosition(centerX, actionButtonContainerY); }
  
    // Update layout *within* the players list container
    this.updatePlayersListLayout();
}
  
// --- ADD THIS NEW HELPER METHOD ---
updatePlayersListLayout() {
    if (!this.playersContainer || !this.playersContainer.list) return;
    const itemSpacing = 45;
    let currentItemIndex = 0;
    this.playersContainer.list.forEach(item => {
        // Assuming items are added in groups (bg, name, status)
        if (item.type === 'Rectangle' && item.getData('isPlayerBg')) { // Add a flag to identify player BGs
            const yPos = currentItemIndex * itemSpacing;
            const bg = item;
            const nameText = item.getData('nameText'); // Assume stored references
            const statusText = item.getData('statusText'); // Assume stored references
  
            if (bg) bg.y = yPos;
            if (nameText) nameText.y = yPos;
            if (statusText) statusText.y = yPos;
            currentItemIndex++;
        }
    });
}
// Modify updatePlayersList to store references on the background rectangle
// Replace the updatePlayersList method in LobbyScene.js with this improved version:
// Replace the updatePlayersList method in LobbyScene.js with this improved version:
updatePlayersList() {
    // Guard clause: Ensure container exists and scene is active
    if (!this.playersContainer || !this.playersContainer.scene || !this.scene?.systems?.scenePlugin?.isActive(this.scene.key)) {
        // console.warn("updatePlayersList: Container/Scene not ready or inactive."); // Optional log
        return;
    }

    const newPlayersData = this.gameData?.players || {};
    const currentPlayerIds = Object.keys(newPlayersData);
    const existingPlayerIds = Array.from(this.playerUiMap.keys());
    const currentUserId = auth?.currentUser?.uid || '';

    // --- Team Name Update ---
    if (this.gameData?.teamName) {
        if (this.uiElements.teamNameText && this.gameData.teamName !== this.uiElements.teamNameText.text) {
            this.teamName = this.gameData.teamName;
            this.uiElements.teamNameText.setText(this.teamName);
            if (this.uiElements.teamNameBorder?.active) {
                try {
                    const bounds = this.uiElements.teamNameText.getBounds();
                    this.uiElements.teamNameBorder.clear().lineStyle(1, 0x00FFFF, 0.4).strokeRect(0, 0, bounds.width + 10, bounds.height + 10);
                    this.uiElements.teamNameBorder.setPosition(this.uiElements.teamNameText.x - bounds.width / 2 - 5, this.uiElements.teamNameText.y - bounds.height / 2 - 5);
                } catch(e) { console.warn("Error updating team name border:", e); }
            }
        }
    }
    // --- End Team Name Update ---

    // --- Player List Diffing ---
    const itemSpacing = 45;
    const itemHeight = 40;
    const itemWidth = 350;

    // Remove players who left
    existingPlayerIds.forEach(playerId => {
        if (!newPlayersData[playerId]) {
            const ui = this.playerUiMap.get(playerId);
            if (ui) { try { ui.bg?.destroy(); ui.nameText?.destroy(); ui.statusText?.destroy(); } catch (e) {} }
            this.playerUiMap.delete(playerId);
        }
    });

    // Update or add current players
    currentPlayerIds.forEach((playerId, index) => {
        const playerData = newPlayersData[playerId];
        const y = index * itemSpacing;
        const isHostPlayer = playerData.isHost ? "👑 " : "";
        const isCurrentUser = playerId === currentUserId;
        const statusTextValue = playerData.status || "waiting";
        const statusColor = statusTextValue === 'waiting' ? '#FFFF00' : '#00FF00';

        let ui = this.playerUiMap.get(playerId);

        if (ui) { // Update existing
             if (ui.bg?.active && ui.nameText?.active && ui.statusText?.active) {
                 if (this.tweens && Math.abs(ui.bg.y - y) > 1) {
                      this.tweens.add({ targets: [ui.bg, ui.nameText, ui.statusText], y: y, duration: 200, ease: 'Sine.easeInOut' });
                 } else {
                      ui.bg.y = y; ui.nameText.y = y; ui.statusText.y = y;
                 }
                 ui.nameText.setText(`${isHostPlayer}${playerData.name || 'Player'}`);
                 ui.nameText.setColor(isCurrentUser ? "#00FF00" : "#FFFFFF");
                 ui.statusText.setText(statusTextValue);
                 ui.statusText.setColor(statusColor);
                 ui.bg.setFillStyle(isCurrentUser ? 0x204020 : (index % 2 === 0 ? 0x282828 : 0x303030), 0.8);
                 ui.bg.setStrokeStyle(isCurrentUser ? 2 : 0, 0x00FF00);
            } else {
                console.warn(`UI elements for player ${playerId} inactive/missing, will recreate.`);
                if(ui) { try { ui.bg?.destroy(); ui.nameText?.destroy(); ui.statusText?.destroy(); } catch(e){} }
                this.playerUiMap.delete(playerId);
                ui = null; // Force recreation below
            }
        }

        if (!ui) { // Create new
            const itemBg = this.add.rectangle(0, y, itemWidth, itemHeight, isCurrentUser ? 0x204020 : (index % 2 === 0 ? 0x282828 : 0x303030), 0.8).setOrigin(0.5);
            if (isCurrentUser) itemBg.setStrokeStyle(2, 0x00FF00);
            const nameText = this.add.text(-itemWidth / 2 + 15, y, `${isHostPlayer}${playerData.name || 'Player'}`, { fontFamily: "monospace", fontSize: "18px", color: isCurrentUser ? "#00FF00" : "#FFFFFF", align: "left" }).setOrigin(0, 0.5);
            const statusText = this.add.text(itemWidth / 2 - 15, y, statusTextValue, { fontFamily: "monospace", fontSize: "16px", color: statusColor, align: "right" }).setOrigin(1, 0.5);

            itemBg.setData('isPlayerBg', true);
            itemBg.setData('nameText', nameText);
            itemBg.setData('statusText', statusText);

            this.playersContainer.add([itemBg, nameText, statusText]);
            this.playerUiMap.set(playerId, { bg: itemBg, nameText, statusText });
        }
    });
    // --- End Player List Diffing ---


    // --- FIX: Added Safety Checks for Start Button Logic ---
    if (this.isHost) {
        // Ensure scene is still active before accessing anything
        if (!this.scene?.systems?.scenePlugin?.isActive(this.scene.key)) {
            // console.log("updatePlayersList: Scene inactive, skipping start button update.");
            return; // Stop processing if scene is shutting down
        }

        const startBtnContainer = this.uiElements?.startBtn; // Use optional chaining

        // Check if the button container exists AND is still part of the active scene AND is active itself
        if (startBtnContainer && startBtnContainer.scene === this && startBtnContainer.active) {
            // Get the internal graphic and text using the keys from MenuUIUtils
            const rect = startBtnContainer.getData?.('rectGraphic'); // Correct key
            const text = startBtnContainer.getData?.('textObject'); // Correct key

            if (rect && text) { // Check internal elements exist
                const canStart = currentPlayerIds.length >= 2;

                if (canStart) {
                    // Enable button - Use MenuUIUtils helpers for consistency
                    MenuUIUtils.resetButtonState(startBtnContainer, "START GAME", MenuUIUtils.COLORS.success);
                    console.debug("Lobby Start button updated: START GAME (Enabled)");
                } else {
                    // Disable button - Use MenuUIUtils helpers for consistency
                    MenuUIUtils.setButtonLoading(startBtnContainer, "WAITING (2+)...", true); // 'true' disables interaction
                    console.debug("Lobby Start button updated: WAITING (Disabled)");
                }
            } else {
                // This might happen briefly during creation/destruction, usually okay unless persistent
                console.warn("Start button UI internal elements (rect/text) missing in updatePlayersList.");
            }
        } else {
             // Log if the button container itself is missing or belongs to another scene (common during transitions)
             // console.warn("Start button container is missing, inactive, or belongs to wrong scene in updatePlayersList."); // Reduce noise, uncomment if needed
        }
    }
    // --- END FIX ---
}
// START OF REPLACEMENT CODE for updatePlayersList

  // END OF REPLACEMENT CODE for updatePlayersList
   shutdown() { /* ... Keep version with UIController.setActiveScene(null) and listener cleanup ... */
       console.log(`Shutting down ${this.scene.key} scene`); UIController.setActiveScene(null); const aiForm=document.getElementById('ai-name-form'); const aiClose=document.getElementById('ai-name-close-btn'); const customForm=document.getElementById('custom-name-form'); const customClose=document.getElementById('custom-name-close-btn'); if(aiForm)aiForm.removeEventListener('submit', this.boundHandleAiSubmit); if(aiClose)aiClose.removeEventListener('click', this.boundHideAiPopup); if(customForm)customForm.removeEventListener('submit', this.boundHandleCustomSubmit); if(customClose)customClose.removeEventListener('click', this.boundHideCustomPopup); this.hidePopup('ai-name-popup'); this.hidePopup('custom-name-popup'); if (this.unsubscribe) { try { this.unsubscribe(); } catch (e) {} this.unsubscribe = null; } this.playerUiMap.forEach(ui => { try { ui.bg?.destroy(); ui.nameText?.destroy(); ui.statusText?.destroy(); } catch(e){} }); this.playerUiMap.clear(); Object.values(this.uiElements).forEach(el => { if (el) { if (el.destroy) { try { el.destroy(); } catch(e){} } else if (el.getData?.('rect')) { try { el.getData('rect')?.destroy(); } catch(e){} try { el.getData('text')?.destroy(); } catch(e){} } } }); this.uiElements = {};
       // Call parent shutdown explicitly using prototype
       try { if(this.parentProtoShutdown) this.parentProtoShutdown.call(this); else super.shutdown(); } catch(e){ console.error("Error in shutdown:", e);}
   }

} // End of LobbyScene class

export default LobbyScene;